import { cn } from '@/lib/utils';
import { size } from 'lodash';
import { Pin, Share2 } from 'lucide-react';
import { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
  ChainlitContext,
  ClientError,
  PINNED_GROUP,
  ThreadHistory, // sessionIdState,
  isThreadPinned,
  threadHistoryState,
  useChatInteract,
  useChatMessages,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

import Alert from '@/components/Alert';
import ShareDialog from '@/components/share/ShareDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/ui/sidebar';
import { useSidebar } from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

import { Translator } from '../i18n';
import { RUN_LABEL, RUN_TEXT_CLASS, RunMark } from '../runs/RunMark';
import { isActive, useRuns } from '../runs/api';
import ThreadOptions from './ThreadOptions';
import { ThreadRowsSkeleton } from './searchShared';

/**
 * The full title on hover, only when the row cuts it, wrapped to a readable
 * width (it used to stretch across the screen in one line). Not on touch.
 */
function TitleTooltip({
  label,
  disabled,
  children
}: {
  label?: string;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  return (
    <Tooltip
      open={open}
      onOpenChange={(next) => {
        if (!next || disabled || !label) return setOpen(false);
        const title = triggerRef.current?.querySelector(
          '[data-thread-title]'
        ) as HTMLElement | null;
        setOpen(!!title && title.scrollWidth > title.clientWidth + 1);
      }}
    >
      <TooltipTrigger asChild ref={triggerRef as any}>
        {children}
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        className="max-w-xs whitespace-normal break-words leading-snug"
      >
        <p dir="auto">{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface ThreadListProps {
  threadHistory?: ThreadHistory;
  error?: string;
  isFetching: boolean;
  isLoadingMore: boolean;
}

export function ThreadList({
  threadHistory,
  error,
  isFetching,
  isLoadingMore
}: ThreadListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { idToResume } = useChatSession();
  const { clear } = useChatInteract();
  const { threadId: currentThreadId } = useChatMessages();
  const [threadIdToDelete, setThreadIdToDelete] = useState<string>();
  const [threadIdToRename, setThreadIdToRename] = useState<string>();
  const [threadNewName, setThreadNewName] = useState<string>();
  const setThreadHistory = useSetRecoilState(threadHistoryState);
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const { isMobile } = useSidebar();
  const { runs } = useRuns();
  const dataPersistence = config?.dataPersistence;
  const threadSharingReady = Boolean((config as any)?.threadSharing);
  // sessionId not needed here

  // Share thread state
  const [threadIdToShare, setThreadIdToShare] = useState<string | undefined>();
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  // Share dialog state is centralized in ShareDialog; we only track which thread to share

  const handleShareThread = (threadId: string) => {
    if (!threadSharingReady) return;
    setThreadIdToShare(threadId);
    setIsShareDialogOpen(true);
    // ShareDialog handles its own internal state; we just open it
  };

  type ParsedGroupLabel = {
    month: string;
    year: number;
    raw: string;
  };

  const getMonthMap = (
    locale = navigator.language
  ): { map: Record<string, number>; monthRegex: RegExp } => {
    const map: Record<string, number> = {};
    const monthNames: string[] = [];

    for (let i = 0; i < 12; i++) {
      const d = new Date(2020, i, 1);

      const long = d
        .toLocaleDateString(locale, { month: 'long' })
        .toLocaleLowerCase(locale);

      map[long] = i;
      monthNames.push(long);
    }
    const monthRegex = new RegExp(`\\b(${monthNames.join('|')})\\b`, 'i');
    return { map, monthRegex };
  };

  const { map: monthMap, monthRegex } = useMemo<{
    map: Record<string, number>;
    monthRegex: RegExp;
  }>(() => getMonthMap(), []);

  const parseGroupLabel = (label: string): ParsedGroupLabel | null => {
    const locale = navigator.language;

    const matchMonth = label.toLocaleLowerCase(locale).match(monthRegex);
    if (!matchMonth) return null;
    const month = matchMonth[0];

    const matchYear = label.match(/\d{4}/);
    if (!matchYear) return null;
    const year = Number(matchYear[0]);

    if (isNaN(year)) return null;

    return { month, year, raw: label };
  };

  const sortGroupsByDate = (a: string, b: string): number => {
    const aParsed = parseGroupLabel(a);
    const bParsed = parseGroupLabel(b);

    if (!aParsed || !bParsed) return a.localeCompare(b);

    if (aParsed.year !== bParsed.year) {
      return bParsed.year - aParsed.year;
    }
    const aMonth = monthMap[aParsed.month] ?? -1;
    const bMonth = monthMap[bParsed.month] ?? -1;

    return bMonth - aMonth;
  };

  const sortedTimeGroupKeys = useMemo(() => {
    if (!threadHistory?.timeGroupedThreads) return [];
    const fixedOrder = [
      PINNED_GROUP,
      'Today',
      'Yesterday',
      'Previous 7 days',
      'Previous 30 days'
    ];
    return Object.keys(threadHistory.timeGroupedThreads).sort((a, b) => {
      const aIndex = fixedOrder.indexOf(a);
      const bIndex = fixedOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return sortGroupsByDate(a, b);
    });
  }, [threadHistory?.timeGroupedThreads]);

  if (isFetching || (!threadHistory?.timeGroupedThreads && isLoadingMore)) {
    return <ThreadRowsSkeleton rows={8} />;
  }

  if (error) {
    return (
      <Alert variant="error" className="m-3">
        {error}
      </Alert>
    );
  }

  if (!threadHistory || size(threadHistory?.timeGroupedThreads) === 0) {
    return (
      <Alert variant="info" className="m-3">
        <Translator path="threadHistory.sidebar.empty" />
      </Alert>
    );
  }

  const handleDeleteThread = async () => {
    if (!threadIdToDelete) return;
    if (
      threadIdToDelete === idToResume ||
      threadIdToDelete === currentThreadId
    ) {
      clear();
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    toast.promise(apiClient.deleteThread(threadIdToDelete), {
      loading: (
        <Translator path="threadHistory.thread.actions.delete.inProgress" />
      ),
      success: () => {
        setThreadHistory((prev) => ({
          ...prev,
          threads: prev?.threads?.filter((t) => t.id !== threadIdToDelete)
        }));
        navigate('/');
        return (
          <Translator path="threadHistory.thread.actions.delete.success" />
        );
      },
      error: (err) => {
        if (err instanceof ClientError) {
          return <span>{err.message}</span>;
        } else {
          return <span></span>;
        }
      }
    });
  };

  // Pin state lives in thread metadata; the list regroups from it (group.ts),
  // so flipping it locally moves the row into or out of "Pinned" at once.
  const handleTogglePin = (threadId: string, pinned: boolean) => {
    toast.promise(apiClient.pinThread(threadId, !pinned), {
      loading: null,
      success: () => {
        setThreadHistory((prev) => {
          if (!prev?.threads) return prev;
          const threads = prev.threads.map((t) =>
            t.id === threadId
              ? {
                  ...t,
                  metadata: {
                    ...(t.metadata || {}),
                    pinned: !pinned,
                    pinned_at: !pinned ? new Date().toISOString() : null
                  }
                }
              : t
          );
          return { ...prev, threads };
        });
        return (
          <Translator
            path={
              pinned
                ? 'threadHistory.thread.actions.pin.unpinned'
                : 'threadHistory.thread.actions.pin.success'
            }
          />
        );
      },
      error: (err) => {
        if (err instanceof ClientError) {
          return <span>{err.message}</span>;
        } else {
          return <span></span>;
        }
      }
    });
  };

  const handleRenameThread = () => {
    if (!threadIdToRename || !threadNewName) return;

    toast.promise(apiClient.renameThread(threadIdToRename, threadNewName), {
      loading: (
        <Translator path="threadHistory.thread.actions.rename.inProgress" />
      ),
      success: () => {
        setThreadNewName(undefined);
        setThreadIdToRename(undefined);
        setThreadHistory((prev) => {
          const next = {
            ...prev,
            threads: prev?.threads ? [...prev.threads] : undefined
          };
          const threadIndex = next.threads?.findIndex(
            (t) => t.id === threadIdToRename
          );
          if (typeof threadIndex === 'number' && next.threads) {
            next.threads[threadIndex] = {
              ...next.threads[threadIndex],
              name: threadNewName
            };
          }
          return next;
        });
        return (
          <div>
            <Translator path="threadHistory.thread.actions.rename.success" />
          </div>
        );
      },
      error: (err) => {
        if (err instanceof ClientError) {
          return <span>{err.message}</span>;
        } else {
          return <span></span>;
        }
      }
    });
  };

  const getTimeGroupLabel = (group: string) => {
    const labels = {
      [PINNED_GROUP]: (
        <Translator path="threadHistory.sidebar.timeframes.pinned" />
      ),
      Today: <Translator path="threadHistory.sidebar.timeframes.today" />,
      Yesterday: (
        <Translator path="threadHistory.sidebar.timeframes.yesterday" />
      ),
      'Previous 7 days': (
        <Translator path="threadHistory.sidebar.timeframes.previous7days" />
      ),
      'Previous 30 days': (
        <Translator path="threadHistory.sidebar.timeframes.previous30days" />
      )
    };
    return labels[group as keyof typeof labels] || group;
  };

  return (
    <>
      <AlertDialog
        open={!!threadIdToDelete}
        onOpenChange={() => setThreadIdToDelete(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Translator path="threadHistory.thread.actions.delete.title" />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Translator path="threadHistory.thread.actions.delete.description" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0">
              <Translator path="common.actions.cancel" />
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteThread}>
              <Translator path="common.actions.confirm" />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={!!threadIdToRename}
        onOpenChange={() => setThreadIdToRename(undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Translator path="threadHistory.thread.actions.rename.title" />
            </DialogTitle>
            <DialogDescription>
              <Translator path="threadHistory.thread.actions.rename.description" />
            </DialogDescription>
          </DialogHeader>
          <div className="my-6">
            <Label htmlFor="name" className="text-right">
              <Translator path="threadHistory.thread.actions.rename.form.name.label" />
            </Label>
            <Input
              id="name"
              required
              value={threadNewName}
              onChange={(e) => setThreadNewName(e.target.value)}
              placeholder={t(
                'threadHistory.thread.actions.rename.form.name.placeholder'
              )}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setThreadIdToRename(undefined)}
            >
              <Translator path="common.actions.cancel" />
            </Button>
            <Button type="button" onClick={handleRenameThread}>
              <Translator path="common.actions.confirm" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ShareDialog
        open={isShareDialogOpen}
        onOpenChange={(open) => {
          setIsShareDialogOpen(open);
          if (!open) {
            setThreadIdToShare(undefined);
          }
        }}
        threadId={threadIdToShare || null}
      />
      <TooltipProvider delayDuration={300}>
        {sortedTimeGroupKeys.map((group) => {
          const items = threadHistory!.timeGroupedThreads![group];
          return (
            <SidebarGroup key={group}>
              <SidebarGroupLabel>{getTimeGroupLabel(group)}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((thread) => {
                    const isResumed =
                      idToResume === thread.id &&
                      !threadHistory!.currentThreadId;
                    const isSelected =
                      isResumed || threadHistory!.currentThreadId === thread.id;
                    const pinned = isThreadPinned(thread);
                    // Ready / didn't-finish are news about a conversation the
                    // user is not looking at; the open one never shows them.
                    const runState = runs[thread.id]?.state;
                    const shownState =
                      runState && !(isSelected && !isActive(runState))
                        ? runState
                        : undefined;
                    return (
                      <SidebarMenuItem
                        key={thread.id}
                        id={`thread-${thread.id}`}
                      >
                        <TitleTooltip label={thread.name} disabled={isMobile}>
                          <Link to={isResumed ? '' : `/thread/${thread.id}`}>
                            <SidebarMenuButton
                              isActive={isSelected}
                              className={cn(
                                'relative group/thread',
                                shownState ? 'h-auto min-h-9 py-1.5' : 'h-9'
                              )}
                            >
                              <span className="flex min-w-0 flex-1 items-center gap-2">
                                {pinned ? (
                                  <Pin
                                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                    aria-hidden="true"
                                  />
                                ) : null}
                                {thread.metadata?.is_shared ? (
                                  <Share2
                                    className="h-4 w-4 shrink-0 text-muted-foreground"
                                    aria-hidden="true"
                                  />
                                ) : null}
                                {/* Thread titles are the user's own question,
                                      so most of them are Arabic. */}
                                <span className="flex min-w-0 flex-1 flex-col">
                                  <span
                                    className="truncate"
                                    dir="auto"
                                    data-thread-title
                                  >
                                    {thread.name || (
                                      <Translator path="threadHistory.thread.untitled" />
                                    )}
                                  </span>
                                  {shownState ? (
                                    <span
                                      className={cn(
                                        'truncate text-[11.5px] leading-tight',
                                        RUN_TEXT_CLASS[shownState]
                                      )}
                                    >
                                      {RUN_LABEL[shownState]}
                                    </span>
                                  ) : null}
                                </span>
                                {shownState ? (
                                  <RunMark
                                    state={shownState}
                                    className="relative z-10 mr-1"
                                  />
                                ) : null}
                              </span>
                              <div
                                className={cn(
                                  'absolute w-10 bottom-0 top-0 right-0 bg-gradient-to-l from-[hsl(var(--sidebar-background))] to-transparent'
                                )}
                              />
                              <ThreadOptions
                                onDelete={() => setThreadIdToDelete(thread.id)}
                                onRename={() => {
                                  setThreadIdToRename(thread.id);
                                  setThreadNewName(thread.name);
                                }}
                                pinned={pinned}
                                onTogglePin={() =>
                                  handleTogglePin(thread.id, pinned)
                                }
                                onShare={
                                  dataPersistence && threadSharingReady
                                    ? () => handleShareThread(thread.id)
                                    : undefined
                                }
                                className={cn(
                                  'absolute z-20 bottom-0 top-0 right-0 bg-sidebar-accent hover:bg-sidebar-accent hover:text-primary flex opacity-0 group-hover/thread:opacity-100 [@media(hover:none)]:opacity-100',
                                  isSelected && 'bg-sidebar-accent opacity-100'
                                )}
                              />
                            </SidebarMenuButton>
                          </Link>
                        </TitleTooltip>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </TooltipProvider>
      {isLoadingMore ? (
        <div className="px-2 pb-2">
          <div className="px-2 pt-1 text-xs text-muted-foreground">
            <Translator path="threadHistory.sidebar.loadingMore" />
          </div>
          <ThreadRowsSkeleton rows={3} />
        </div>
      ) : null}
    </>
  );
}
